// BRIEF 135 §1(b) — THE THEME'S VARIABLE SURFACE, DECLARED ONCE FOR BOTH PLANES.
//
// ── WHY THIS FILE EXISTS, AND WHY IT IS NOT A COMMENT ───────────────────────
//
// `server/src/routes/themes.js` carried a hand-maintained `ALLOWED_VAR_KEYS`
// whitelist that had to agree with the client's declarations, and it did not.
// `--nwd` joined `OVERRIDABLE_DERIVED_VARS` in 132 §7 and was never added
// there, so every override of it was accepted by the editor, accepted by the
// Save, and dropped by `sanitizeVars` on the way into the database. Nothing
// failed; the row simply reloaded derived. Laurent reported it as "change does
// not stick even after saved".
//
// THAT WAS THE SECOND TIME. The first was Phase 8 Stage 3 (`--ct`/`--or`/
// `--cb`/`--bf`), and the repair then was A COMMENT, three lines above the
// list, naming this exact failure mode and telling the next reader to keep the
// two in step. It is still there. It did not work, because a comment is read by
// whoever happens to open the file and the person adding a token to
// `themeDerivation.ts` has no reason to open this one.
//
// §1(b) offered two mechanisms: derive the server's set from the client's
// declarations, or add a check that fails when they diverge. **DERIVING IS
// CHOSEN, and the reason is that the two are not equally strong.** A check
// reports a divergence that has already happened and can only fire on a tree
// somebody remembered to run it against; a single declaration cannot diverge at
// all. The failure this file closes is not "we noticed too late", it is "there
// were two lists".
//
// ── WHY AN `.mjs`, AND THE PRECEDENT ───────────────────────────────────────
// The server is CommonJS and cannot `require` a `.ts`. This tree already
// carries exactly this shape for exactly this reason — one pure ESM module
// under `src/lib/`, imported by the client and `require`d by the server:
//
//   src/lib/cddIdentity.mjs      server/src/routes/cdd.js:42
//   src/lib/mentionTokens.mjs    server/src/routes/chatMessages.js:17
//   src/lib/caseRefPattern.mjs   server/src/lib/caseRefSettings.js:33
//   src/lib/iso3166.mjs          src/utils/taxonomyMap.ts:2 (client side too)
//
// `themeDerivation.ts` re-exports both names, so every client call site is
// unchanged and nothing has to learn a new import path.
//
// PURE DATA: no imports, no runtime, no colour arithmetic. The derivation
// itself stays in `themeDerivation.ts` — the server has no business computing a
// theme, it only has to know which keys a theme may carry.

/** The colours an admin picks outright, in editor order.
 *
 *  @type {ReadonlyArray<string>} */
export const EDITABLE_VARS = [
  "--bd",
  "--bs",
  "--bc",
  "--bh",
  "--bi",
  "--br",
  "--tp",
  "--ct",
  "--ac",
  "--pp",
  "--or",
  "--gn",
  "--am",
  "--rd",
  // Phase 16 — muted variants of --gn / --rd, used by the LibraryView
  // PAPER checkbox to signal borrow state (available vs out). Editable
  // so admins can re-tune for high-contrast themes.
  "--gnm",
  "--rdm",
  "--cb",
  "--bf",
  /* ── 135 §2(a) — THE BRAND COLOUR ─────────────────────────────────────────
     RULED (Laurent, 25 Aug 2026): the two black/white shades should tint "from
     and towards the logo colour". The firm's logo is an IMAGE
     (`schema.prisma:1460 logo String?`, rendered as an `<img>` at
     `App.tsx:13362`) and there is no logo colour anywhere in the tree, so the
     ruling gives the theme a colour of its own instead. The image is NOT
     sampled.

     IT IS HERE, IN `EDITABLE_VARS`, AND NOT IN `OVERRIDABLE_DERIVED_VARS`.
     §2(a) calls it "an OVERRIDABLE DERIVED var" and in the same breath says it
     "appears as a picker beside the accents, not in the derived list, because
     it is a choice a firm makes rather than a consequence it inspects". Those
     two cannot both be arrays: membership of `OVERRIDABLE_DERIVED_VARS` is
     exactly what puts a var in the editor's "Derived overrides" section, behind
     a tick-box. The BEHAVIOUR the section describes — absent, it is the accent —
     is `deriveThemeVars`'s job and is implemented there, and the PLACEMENT the
     section asks for is this array. Both halves of §2(a) hold; only the word
     does not, and it is recorded rather than worked around.

     Being in either array is what makes it storable (see `STORABLE_VARS`), so
     it inherits §1's fix exactly as §2(a) intends: nothing about the route had
     to learn a new key.

     THE NAME IS SPELLED OUT, against this file's own two-and-three-letter
     convention, and the reason is collision. Every short abbreviation of
     "brand" is one character from `--br`, the BORDER — and CSS custom
     properties are exact-match, so `var(--br)` written where `var(--brd)` was
     meant does not fail, it silently paints the border colour. A wrong colour
     is worse than an error. The editor renders LABELS, not names, so nothing a
     user reads gets longer. */
  "--brand",
];

/** Derived by default, admin-overridable per theme.
 *
 *  `--ts` / `--tm` are computed from `--tp` and `--bs`; 111 §5d added `--hlj`
 *  (defaults to `--am`) and `--hll` (defaults to `--pp`); 132 §7 added `--nwd`
 *  (from the surface). The remaining derived vars are alpha-based and are NOT
 *  overridable — they always follow their parent exactly, and `--shd` could not
 *  be saved even if it were (see the note on the hex rule below).
 *
 *  @type {ReadonlyArray<string>} */
/* 164 §A.7 — `--st`, the STATED-amount text colour (a member's due that was
 * stated by hand rather than derived from the round's formula). Derived from
 * the accent in `deriveThemeVars` (ice-blue: the accent pulled toward the
 * text pole of the surface), resolves to six-hex, so it may sit here and be
 * stored — declared in THIS file precisely because the server copy fell
 * behind twice before this file existed. */
export const OVERRIDABLE_DERIVED_VARS = ["--ts", "--tm", "--hlj", "--hll", "--nwd", "--st"];

/**
 * Every key a stored theme may carry — the union, and the server's whole
 * question.
 *
 * ── THE SIX-HEX RULE, AND WHICH VARS IT EXCLUDES (135 §1) ──────────────────
 * `themes.js` also requires `/^#[0-9a-fA-F]{6}$/`, so a token whose value is
 * `rgba(...)` can never persist through that route whatever this list says.
 * Measured against `deriveThemeVars`, the vars that produce `rgba` are:
 *
 *     --shd  --as  --ag  --ps  --gs  --ams  --rs
 *
 * **NONE OF THEM IS IN THIS UNION**, and that is not a coincidence: all seven
 * are alpha shades of a parent, which is exactly what makes them non-
 * overridable. So the hex rule excludes nothing that can be saved, and the two
 * constraints agree today.
 *
 * IT IS A CONSTRAINT ON WHAT MAY JOIN THIS LIST. **A var that resolves to
 * `rgba` may never be added to either array above** — it would be accepted by
 * the editor and dropped by the route, which is the precise defect this file
 * was created to end.
 *
 * @type {ReadonlyArray<string>}
 */
export const STORABLE_VARS = [...EDITABLE_VARS, ...OVERRIDABLE_DERIVED_VARS];
